import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBackButton from '../common/AppBackButton';
import {
  Bell,
  ChevronRight,
  Clock,
  Download,
  Key,
  Lock,
  Mail,
  Puzzle,
  Share2,
  Shield,
  Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';

import axiosInstance from '../../api/api';
import { getUserFacingError } from '../../utils/userFacingError';
import { API_ROUTES } from '../../config/api-routes';
import './SecurityPage.css';

type SecurityNotifications = {
  loginAlerts: boolean;
  securityUpdates: boolean;
};

const DEFAULT_SECURITY_NOTIFICATIONS: SecurityNotifications = {
  loginAlerts: true,
  securityUpdates: true,
};

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<SecurityNotifications>(
    DEFAULT_SECURITY_NOTIFICATIONS
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get('user/current-user');
        const user = (res.data?.user ?? res.data) as Record<string, unknown>;
        const raw = user.securityNotifications ?? user.security_notifications;
        if (active && raw && typeof raw === 'object') {
          setNotifications({
            ...DEFAULT_SECURITY_NOTIFICATIONS,
            ...(raw as SecurityNotifications),
          });
        }
      } catch {
        /* use defaults */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const persistNotifications = async (next: SecurityNotifications) => {
    setNotifications(next);
    setSaving(true);
    try {
      await axiosInstance.put(
        API_ROUTES.USER.UPDATE_USER,
        { securityNotifications: next },
        { suppressErrorToast: true }
      );
    } catch (err) {
      toast.error(
        getUserFacingError(
          err,
          'We could not save your security notification preferences. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="security-page">
      <div className="security-page__toolbar">
        <AppBackButton className="security-page__back-btn" />
      </div>

      <header className="security-page__head">
        <h1 className="security-page__title">Security</h1>
        <p className="security-page__subtitle">
          Manage your account security and keep your data safe.
        </p>
      </header>

      <section className="security-page__card">
        <div className="security-page__card-head">
          <div className="security-page__card-icon" aria-hidden>
            <Shield size={20} />
          </div>
          <div>
            <h2 className="security-page__card-title">Account security</h2>
            <p className="security-page__card-desc">
              Keep your account protected with these security essentials.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="security-page__row"
          onClick={() => navigate('/user-settings')}
        >
          <Lock className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Change password</strong>
          </span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>

        <button
          type="button"
          className="security-page__row"
          onClick={() => toast.info('Two-factor authentication is coming soon.')}
        >
          <Key className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Two-factor authentication (2FA)</strong>
          </span>
          <span className="security-page__badge">Soon</span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>

        <button
          type="button"
          className="security-page__row"
          onClick={() => toast.info('Login activity history is coming soon.')}
        >
          <Clock className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Login activity</strong>
          </span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>
      </section>

      <section className="security-page__card">
        <div className="security-page__card-head">
          <div>
            <h2 className="security-page__card-title">Active sessions</h2>
            <p className="security-page__card-desc">
              You&apos;re currently signed in on these devices.
            </p>
          </div>
        </div>

        <div className="security-page__session">
          <Smartphone className="security-page__row-icon" size={22} aria-hidden />
          <div>
            <div className="security-page__session-name">
              This device
              <span className="security-page__this-device">This device</span>
            </div>
            <p className="security-page__session-meta">
              <span className="security-page__session-active">Active now</span>
            </p>
          </div>
        </div>

        <button
          type="button"
          className="security-page__link-row"
          onClick={() => toast.info('Session management is coming soon.')}
        >
          View all other sessions
          <ChevronRight size={16} aria-hidden />
        </button>
      </section>

      <section className="security-page__card">
        <div className="security-page__card-head">
          <div>
            <h2 className="security-page__card-title">Security notifications</h2>
            <p className="security-page__card-desc">
              Get notified about important security events.
            </p>
          </div>
        </div>

        <div className="security-page__toggle-row">
          <Bell className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Login alerts</strong>
            <span>Get notified of new logins to your account.</span>
          </span>
          <label className="security-page__switch">
            <input
              type="checkbox"
              checked={notifications.loginAlerts}
              disabled={saving}
              onChange={(e) =>
                void persistNotifications({
                  ...notifications,
                  loginAlerts: e.target.checked,
                })
              }
            />
            <span className="security-page__slider" />
          </label>
        </div>

        <div className="security-page__toggle-row">
          <Mail className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Security updates</strong>
            <span>Receive important security updates and tips.</span>
          </span>
          <label className="security-page__switch">
            <input
              type="checkbox"
              checked={notifications.securityUpdates}
              disabled={saving}
              onChange={(e) =>
                void persistNotifications({
                  ...notifications,
                  securityUpdates: e.target.checked,
                })
              }
            />
            <span className="security-page__slider" />
          </label>
        </div>
      </section>

      <section className="security-page__card">
        <div className="security-page__card-head">
          <div>
            <h2 className="security-page__card-title">Data &amp; access</h2>
          </div>
        </div>

        <button
          type="button"
          className="security-page__row"
          onClick={() => toast.info('Data export is coming soon.')}
        >
          <Download className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Download your data</strong>
          </span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>

        <button
          type="button"
          className="security-page__row"
          onClick={() => navigate('/account/connections')}
        >
          <Share2 className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Manage connected accounts</strong>
          </span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>

        <button
          type="button"
          className="security-page__row"
          onClick={() => toast.info('Apps and integrations are coming soon.')}
        >
          <Puzzle className="security-page__row-icon" size={20} aria-hidden />
          <span className="security-page__row-text">
            <strong>Apps and integrations</strong>
          </span>
          <ChevronRight className="security-page__row-chevron" size={18} />
        </button>
      </section>
    </div>
  );
};

export default SecurityPage;
